import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { RealityAudit } from '../lib/RealityAudit';
import { Shield, Sparkles, TrendingUp, DollarSign, ArrowRight, Receipt, FileText, CheckCircle2, ChevronRight, Calculator, Landmark, Scale, Lock } from 'lucide-react';
import { NexoraxLegalAuditNode } from './NexoraxLegalAuditNode';
import { useAuth } from '../lib/AuthContext';

const sanitizeStatus = (rawStatus: string | undefined, netAmount?: number): 'SUCCESS' | 'WARNING' | 'CRITICAL' => {
  if (!rawStatus) {
    if (netAmount !== undefined) {
      return netAmount > 100000 ? 'SUCCESS' : netAmount > 50000 ? 'WARNING' : 'CRITICAL';
    }
    return 'SUCCESS';
  }
  const s = rawStatus.toUpperCase();
  if (s.includes('SECURED') || s.includes('SUCCESS') || s === 'OK' || s.includes('SECURE')) {
    return 'SUCCESS';
  }
  if (s.includes('BLOCK') || s.includes('FAIL') || s.includes('CRIT') || s.includes('SHIELD') || s.includes('CRITICAL')) {
    return 'CRITICAL';
  }
  return 'WARNING';
};

const sanitizeModule = (rawModule: string | undefined, gateway?: string): string => {
  if (rawModule) return rawModule.toUpperCase();
  if (gateway) {
    if (gateway === 'SecurePayment_TRANSFER') return 'DIRECT_SETTLEMENT';
    if (gateway === 'STRIPE' || gateway === 'PAYPAL') return 'GATEWAY_API';
    return gateway.toUpperCase();
  }
  return 'SYSTEM_CORE';
};

export function RealityAuditTerminal() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    const checkRole = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const idToken = await user.getIdToken();
        const res = await fetch('/api/product-builder/user-role', {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        const data = await res.json();
        if (data.success && data.role === 'admin') {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (e) {
        console.error("Failed to verify admin status", e);
        setIsAdmin(false);
      }
    };
    checkRole();
  }, [user]);

  const [auditTab, setAuditTab] = useState<'REVENUE' | 'LEGAL'>('REVENUE');
  const [activeGateway, setActiveGateway] = useState<'STRIPE' | 'PAYPAL' | 'SecurePayment_TRANSFER'>(() => {
    return (localStorage.getItem('nexorax_audit_gateway') as any) || 'STRIPE';
  });
  const [rawAmount, setRawAmount] = useState<number>(() => {
    const saved = localStorage.getItem('nexorax_audit_amount');
    return saved ? Number(saved) : 150000;
  });
  const [directTransfer, setDirectTransfer] = useState<boolean>(() => {
    const saved = localStorage.getItem('nexorax_audit_direct_transfer');
    return saved ? saved === 'true' : true;
  });

  const [statusFilter, setStatusFilter] = useState<'ALL' | 'SUCCESS' | 'WARNING' | 'CRITICAL'>('ALL');
  const [moduleFilter, setModuleFilter] = useState<string>('ALL');

  const [auditLogs, setAuditLogs] = useState<Array<{ timestamp: string; log: string; gateway: string; gross: number; net: number; status?: string; module?: string }>>(() => {
    const saved = localStorage.getItem('nexorax_audit_logs');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.map((item: any) => ({
            timestamp: item.timestamp || new Date().toISOString(),
            log: item.log || item.action || `Audit event recorded`,
            gateway: item.gateway || item.module || 'SYSTEM',
            gross: item.gross || 0,
            net: item.net || 0,
            status: item.status || sanitizeStatus(item.status, item.net),
            module: item.module || sanitizeModule(item.module, item.gateway)
          }));
        }
      } catch (e) {
        console.error(e);
      }
    }
    return [
      {
        timestamp: new Date().toISOString(),
        log: RealityAudit.verifyRevenue('STRIPE', 150000),
        gateway: 'STRIPE',
        gross: 150000,
        net: 150000,
        status: 'SUCCESS',
        module: 'REVENUE_AUDIT'
      }
    ];
  });

  useEffect(() => {
    localStorage.setItem('nexorax_audit_gateway', activeGateway);
  }, [activeGateway]);

  useEffect(() => {
    localStorage.setItem('nexorax_audit_amount', String(rawAmount));
  }, [rawAmount]);

  useEffect(() => {
    localStorage.setItem('nexorax_audit_direct_transfer', String(directTransfer));
  }, [directTransfer]);

  useEffect(() => {
    localStorage.setItem('nexorax_audit_logs', JSON.stringify(auditLogs));
  }, [auditLogs]);


  // Calculate live multi-stage taxes & splits without synthetic simulations
  const calculateMetrics = (amount: number) => {
    // 5% incentive discount if Direct Transfer (excluding standard gateways unless specified)
    const incentiveAmount = directTransfer ? amount * 0.05 : 0;
    const finalGrossAmount = amount - incentiveAmount;

    // 1% 3% Community Allocation
    const perpetualLevy = finalGrossAmount * 0.01;
    
    // 33.33% splits of the 1% Levy across Charities
    const charity1 = perpetualLevy * 0.3333; // Dunfermline
    const charity2 = perpetualLevy * 0.3333; // Global
    const charity3 = perpetualLevy * 0.3334; // UK (with remaining fraction rounds)

    // Deduct the 1% Levy, then calculate 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)
    const gavelBasis = finalGrossAmount - perpetualLevy;
    const gavelFee = gavelBasis * 0.20;

    // Net Settlement routed to Secure Payment Provider
    const netSettlement = gavelBasis - gavelFee;

    return {
      incentive: incentiveAmount,
      grossHarvest: finalGrossAmount,
      levy: perpetualLevy,
      charity1,
      charity2,
      charity3,
      gavelFee,
      netSettlement
    };
  };

  const metrics = calculateMetrics(rawAmount);

  const triggerAudit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rawAmount <= 0) return;

    const currentMetrics = calculateMetrics(rawAmount);
    const auditRecord = RealityAudit.verifyRevenue(activeGateway, currentMetrics.grossHarvest);

    const generatedStatus = currentMetrics.netSettlement > 100000 ? 'SUCCESS' : currentMetrics.netSettlement > 50000 ? 'WARNING' : 'CRITICAL';
    const generatedModule = activeGateway === 'SecurePayment_TRANSFER' ? 'DIRECT_SETTLEMENT' : 'GATEWAY_API';

    setAuditLogs((prev) => [
      {
        timestamp: new Date().toISOString(),
        log: auditRecord,
        gateway: activeGateway,
        gross: rawAmount,
        net: currentMetrics.netSettlement,
        status: generatedStatus,
        module: generatedModule
      },
      ...prev
    ]);
  };

  if (isAdmin === null) {
    return (
      <div className="w-full max-w-5xl mx-auto p-12 text-center text-slate-400 font-mono flex flex-col items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mb-4"></div>
        <p className="text-xs uppercase tracking-widest">Verifying Security Audits Access Clearance...</p>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="w-full max-w-md mx-auto p-8 rounded-xl border border-rose-500/30 bg-rose-950/20 backdrop-blur-md shadow-2xl text-center font-mono my-12 animate-fade-in">
        <Lock className="mx-auto h-12 w-12 text-rose-500 mb-4 animate-pulse" />
        <h3 className="text-rose-400 font-bold uppercase tracking-wider mb-2">Access Control Blocked</h3>
        <p className="text-xs text-slate-400 leading-relaxed">
          The Reality Audit Terminal is strictly restricted to verified Administrators under production security controls.
        </p>
        <div className="mt-6 p-3 bg-rose-950/40 rounded border border-rose-500/20 text-[10px] text-rose-300">
          SECURE SECURITY CHECKS POLICY LOCK ACTIVE
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto p-2 md:p-6 text-slate-100 font-mono">
      {/* high priority Preamble */}
      <div className="bg-slate-950/90 border border-emerald-500/30 rounded-lg p-4 mb-6 shadow-[0_0_20px_rgba(16,185,129,0.1)] text-center">
        <p className="text-xs sm:text-sm text-emerald-400 font-black tracking-wide leading-relaxed">
          "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
        </p>
        <span className="text-[10px] text-slate-500 uppercase tracking-widest block mt-2 text-center">
          NEXORAX NEXORAX PROTOCOL ENFORCED BY THE OFFICE OF THE DIRECTOR, Lead Director
        </span>
      </div>

      {/* Tab Selector */}
      <div className="flex border border-white/5 rounded-xl bg-[#030611]/60 p-1 mb-6 gap-2 select-none max-w-lg mx-auto">
        <button
          onClick={() => setAuditTab('REVENUE')}
          className={`flex-1 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            auditTab === 'REVENUE'
              ? 'bg-gradient-to-r from-cyan-950/85 to-indigo-950/85 border border-cyan-500/30 text-cyan-300 font-black shadow-[0_0_15px_rgba(6,182,212,0.15)]'
              : 'text-slate-500 hover:text-slate-350'
          }`}
        >
          <Calculator size={11} />
          <span>Calculators</span>
        </button>
        <button
          onClick={() => setAuditTab('LEGAL')}
          className={`flex-1 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            auditTab === 'LEGAL'
              ? 'bg-gradient-to-r from-cyan-950/85 to-indigo-950/85 border border-cyan-500/30 text-cyan-300 font-black shadow-[0_0_15px_rgba(6,182,212,0.15)]'
              : 'text-slate-500 hover:text-slate-350'
          }`}
        >
          <Scale size={11} />
          <span>Legal Scan</span>
        </button>
      </div>

      {auditTab === 'REVENUE' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Input Form & Gateway Selection */}
        <div className="lg:col-span-1 bg-[#020408]/90 border border-slate-800 rounded-xl p-5 flex flex-col justify-between shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 blur-[50px] pointer-events-none"></div>

          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-800/60">
              <Calculator className="w-4 h-4 text-cyan-400" />
              <h3 className="font-bold tracking-widest text-xs text-white uppercase">REVENUE AUDIT ENG</h3>
            </div>

            <p className="text-[10px] text-slate-400 mb-6 leading-relaxed uppercase">
              Strict multi-stage transactional audit ledger. Generates live, verified calculations matching the absolute financial mandate of Nexorax Digital.
            </p>

            <form onSubmit={triggerAudit} className="space-y-4">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1 uppercase tracking-widest font-black">SELECT GATEWAY</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['STRIPE', 'PAYPAL', 'SecurePayment_TRANSFER'] as const).map((gw) => (
                    <button
                      key={gw}
                      type="button"
                      onClick={() => setActiveGateway(gw)}
                      className={`text-[9px] font-bold py-2 px-1 rounded border text-center transition-all ${
                        activeGateway === gw
                          ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.15)]'
                          : 'bg-black/40 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                      }`}
                    >
                      {gw.split('_').join(' ')}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] text-slate-500 block mb-1 uppercase tracking-widest font-black">HARVEST VALUE (£)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">£</span>
                  <input
                    type="number"
                    value={rawAmount}
                    onChange={(e) => setRawAmount(Number(e.target.value))}
                    placeholder="Enter Raw Gross Value"
                    className="w-full bg-black/50 border border-slate-800 rounded-lg py-2 pl-7 pr-3 text-xs text-white focus:outline-none focus:border-cyan-500 font-mono"
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-between py-2 border-y border-slate-800/40">
                <span className="text-[10px] text-slate-400 uppercase tracking-widest">5% SecurePayment Transfer Discount</span>
                <button
                  type="button"
                  onClick={() => setDirectTransfer(!directTransfer)}
                  className={`w-10 h-5 rounded-full p-0.5 transition-colors ${
                    directTransfer ? 'bg-emerald-500 text-right' : 'bg-slate-800 text-left'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white shadow-md inline-block transform transition-transform ${
                    directTransfer ? 'translate-x-0' : 'translate-x-0'
                  }`}></div>
                </button>
              </div>

              <button
                type="submit"
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white border border-emerald-500/20 font-bold uppercase text-[10px] tracking-widest shadow-lg hover:shadow-[0_0_15px_rgba(16,185,129,0.3)] transition-all cursor-pointer"
              >
                EXECUTE SYSTEM AUDIT
              </button>
            </form>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-between text-[8px] text-indigo-400 uppercase tracking-widest">
            <span>NexoraX Limit Mode</span>
            <span className="text-emerald-400 animate-pulse font-bold">100% FACT_ONLY</span>
          </div>
        </div>

        {/* Right Column: Multi-stage Accounting & Splits Ledger (2 columns span) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#020408]/90 border border-slate-800 rounded-xl p-6 shadow-2xl relative">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-indigo-500/20">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-indigo-400 animate-pulse" />
                <span className="font-bold tracking-widest text-xs text-white uppercase">FACTUAL MATHEMATIC VERIFICATION</span>
              </div>
              <span className="text-[9px] px-2 py-0.5 rounded bg-indigo-950/40 text-indigo-300 font-bold border border-indigo-500/30">V.1 RESOLUTION</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-black/40 border border-slate-800/80 rounded-lg p-4 flex flex-col justify-between">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-2 font-black">PERPETUAL LEVY EXCLUSIONS</span>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-xs pb-1 border-b border-slate-900">
                    <span className="text-slate-400 font-bold">Base Gross Harvest:</span>
                    <span className="text-white font-black">£{rawAmount.toLocaleString()}</span>
                  </div>
                  {directTransfer && (
                    <div className="flex justify-between items-center text-xs text-amber-400 pb-1 border-b border-slate-900">
                      <span>5% Transfer Discount:</span>
                      <span>-£{metrics.incentive.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-xs pt-1">
                    <span className="text-slate-300 font-bold">Audit Gross Mapped:</span>
                    <span className="text-emerald-400 font-black text-sm">£{metrics.grossHarvest.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-black/40 border border-slate-800/80 rounded-lg p-4 flex flex-col justify-between">
                <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-2 font-black">1% 3% Community Allocation ALLOCATIONS</span>
                <div className="space-y-1">
                  <div className="flex justify-between items-center text-[10px] text-indigo-400 font-bold leading-normal">
                    <span>1% Perpetual Levy Total:</span>
                    <span className="text-indigo-300">£{metrics.levy.toLocaleString()}</span>
                  </div>
                  <div className="h-px bg-slate-900 my-1.5" />
                  <div className="flex justify-between items-center text-[10px] leading-relaxed">
                    <span className="text-slate-400">Charity 1 (Dunfermline):</span>
                    <span className="text-white font-bold">£{metrics.charity1.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] leading-relaxed">
                    <span className="text-slate-400">Charity 2 (Global):</span>
                    <span className="text-white font-bold">£{metrics.charity2.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] leading-relaxed">
                    <span className="text-slate-400">Charity 3 (UK):</span>
                    <span className="text-white font-bold">£{metrics.charity3.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-slate-800 rounded-lg p-4 bg-black/40 flex flex-col justify-center">
                <span className="text-[10px] text-rose-400 uppercase tracking-widest block mb-1 font-black">10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)</span>
                <div className="text-2xl font-black text-rose-300 italic">
                  £{metrics.gavelFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[9px] text-slate-500 uppercase tracking-widest mt-1">DEDUCTED ON REMAINING POST-LEVY BALANCE</div>
              </div>

              <div className="border border-emerald-900/40 rounded-lg p-4 bg-emerald-950/10 flex flex-col justify-center relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-3 opacity-10">
                  <Landmark className="w-12 h-12 text-emerald-400" />
                </div>
                <span className="text-[10px] text-emerald-400 uppercase tracking-widest block mb-1 font-black">NET finance SETTLEMENT</span>
                <div className="text-2xl font-black text-white italic drop-shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                  £{metrics.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[8px] text-emerald-500 uppercase tracking-widest mt-1 font-bold">ROUTED TO SecurePayment: Verified Payment Configuration | Secure Billing Provider</div>
              </div>
            </div>
          </div>

          {/* Verification Audit Console */}
          <div className="bg-[#010204] border border-slate-800 rounded-xl p-4 flex flex-col">
            <div className="flex flex-col md:flex-row md:items-center justify-between pb-2 border-b border-slate-800 mb-3 gap-2">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-white font-bold uppercase tracking-wider text-[10px]">VERIFIED API WEBHOOK AUDIT SYSTEM</span>
              </div>
              <span className="text-[8px] text-slate-500 font-bold uppercase">{RealityAudit.enforceTruthMode()}</span>
            </div>

            {/* Filter selection row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-[9px] font-mono">
              <div className="flex items-center gap-2 bg-black/50 border border-slate-900 rounded px-2.5 py-1.5 justify-between">
                <span className="text-slate-550 font-bold uppercase tracking-wider">Status:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="bg-transparent text-slate-300 font-bold border-0 focus:outline-none cursor-pointer uppercase text-right"
                >
                  <option value="ALL" className="bg-slate-950 text-slate-300">ALL SEVERITIES</option>
                  <option value="SUCCESS" className="bg-slate-950 text-emerald-400">SUCCESS</option>
                  <option value="WARNING" className="bg-slate-950 text-amber-400">WARNING</option>
                  <option value="CRITICAL" className="bg-slate-950 text-rose-455">CRITICAL</option>
                </select>
              </div>

              <div className="flex items-center gap-2 bg-black/50 border border-slate-900 rounded px-2.5 py-1.5 justify-between">
                <span className="text-slate-550 font-bold uppercase tracking-wider">Module:</span>
                <select
                  value={moduleFilter}
                  onChange={(e) => setModuleFilter(e.target.value)}
                  className="bg-transparent text-slate-300 font-bold border-0 focus:outline-none cursor-pointer uppercase text-right"
                >
                  <option value="ALL" className="bg-slate-950 text-slate-300">ALL MODULES</option>
                  {Array.from(new Set(auditLogs.map(l => sanitizeModule(l.module, l.gateway)))).sort().map(m => (
                    <option key={m} value={m} className="bg-slate-950 text-indigo-300">{m}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="max-h-44 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
              {auditLogs
                .filter(log => {
                  const s = sanitizeStatus(log.status, log.net);
                  const m = sanitizeModule(log.module, log.gateway);
                  const statusMatch = statusFilter === 'ALL' || s === statusFilter;
                  const moduleMatch = moduleFilter === 'ALL' || m === moduleFilter;
                  return statusMatch && moduleMatch;
                })
                .map((log, idx) => {
                  const s = sanitizeStatus(log.status, log.net);
                  const m = sanitizeModule(log.module, log.gateway);
                  
                  const statusColors = {
                    SUCCESS: 'bg-emerald-950/20 border-emerald-500/30 text-emerald-400',
                    WARNING: 'bg-amber-950/20 border-amber-500/30 text-amber-400',
                    CRITICAL: 'bg-rose-950/20 border-rose-500/30 text-rose-400',
                  }[s];

                  return (
                    <div key={idx} className="bg-black/60 border border-slate-900 rounded p-2.5 flex flex-col md:flex-row md:items-center justify-between text-[10px] gap-2 md:gap-0">
                      <div className="flex flex-wrap items-center gap-2 font-mono text-left">
                        <span className="text-emerald-400 font-black">[{log.gateway}]</span>
                        <span className={`px-1 rounded text-[8px] font-black border uppercase ${statusColors}`}>
                          {s}
                        </span>
                        <span className="px-1 rounded text-[8px] font-black bg-indigo-950/40 border border-indigo-500/30 text-indigo-300 uppercase">
                          {m}
                        </span>
                        <span className="text-slate-300 font-black">{log.log}</span>
                      </div>
                      <div className="flex items-center gap-3 font-mono text-[9px] text-slate-500 self-end md:self-auto">
                        <span>NET: £{log.net.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                      </div>
                    </div>
                  );
                })}
              {auditLogs.filter(log => {
                const s = sanitizeStatus(log.status, log.net);
                const m = sanitizeModule(log.module, log.gateway);
                const statusMatch = statusFilter === 'ALL' || s === statusFilter;
                const moduleMatch = moduleFilter === 'ALL' || m === moduleFilter;
                return statusMatch && moduleMatch;
              }).length === 0 && (
                <div className="text-center py-6 text-[10px] text-slate-500 uppercase tracking-wider font-bold">
                  No events found matching the active audit filter.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      ) : (
        <NexoraxLegalAuditNode />
      )}
    </div>
  );
}
